import { KeyboardEvent as ReactKeyboardEvent, MouseEvent } from "react";

import { css } from "@compiled/react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { media } from "@atlaskit/primitives";
import { ProductKeys } from "@atlassiansox/asc-stargate";

import { templateDetailsSectionRelayFragment$key } from "@/__generated__/templateDetailsSectionRelayFragment.graphql";
import { useSiteSelectorContext } from "@/components/site-selector-modal";

import { TemplateCtaButtons } from "../../template-details-page/template-cta-buttons";
import { TemplateDescription } from "../components/template-description-relay";
import { TemplatePreview } from "../components/template-preview-relay";
import { TemplateRelatedTemplates } from "../components/template-related-templates-relay";

import { TemplateTabbedSection } from "./template-tabbed-section-relay";

interface TemplateDetailsSectionProps {
    template: templateDetailsSectionRelayFragment$key;
    isCfind3646Treatment: boolean;
}

const WHITEBOARD_TEMPLATE_ID = "1HlskYfRNciSnQ02SDhCPs";

const templateDetailsSectionStyles = css({
    [media.below.sm]: {
        height: "auto"
    }
});

const ctaButtonContainerStyles = css({
    display: "none",
    [media.below.sm]: {
        display: "block",
        marginBlockStart: "64px"
    }
});

const previewSectionStyles = css({
    cursor: "pointer"
});

const TemplateDetailsSectionFragment = graphql`
    fragment templateDetailsSectionRelayFragment on ContentPlatformTemplate {
        name
        pluginModuleKey
        templateType {
            id
            templateTypeName
        }
        ...templatePreviewRelayFragment
        ...templateDescriptionRelayFragment
        ...templateTabbedSectionRelayFragment
        ...templateRelatedTemplatesRelayFragment
    }
`;

export const TemplateDetailsSection = ({
    template: templateRef,
    isCfind3646Treatment: isCfind3646Treatment
}: TemplateDetailsSectionProps) => {
    const template = useFragment(TemplateDetailsSectionFragment, templateRef);
    const { pluginModuleKey, name, templateType } = template;
    const { openModalForTemplate } = useSiteSelectorContext();

    const buttonUseTemplateClicked = () => {
        openModalForTemplate({
            templateId: pluginModuleKey ?? "",
            templateName: name ?? "",
            productKey: ProductKeys.CONFLUENCE
        });
    };

    const navigateToSignupClick = (event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        event.preventDefault();
        buttonUseTemplateClicked();
    };

    const navigateToSignupKeyDown = (event: ReactKeyboardEvent) => {
        if (event.code === "Space" || event.code === "Enter") {
            event.stopPropagation();
            event.preventDefault();
            buttonUseTemplateClicked();
        }
    };

    const isWhiteBoardTemplate =
        templateType?.[0]?.id === WHITEBOARD_TEMPLATE_ID;

    return (
        <div
            css={templateDetailsSectionStyles}
            data-testid="TemplateDetailsSection"
        >
            <div
                onClick={
                    isWhiteBoardTemplate ? undefined : navigateToSignupClick
                }
                onKeyDown={
                    isWhiteBoardTemplate ? undefined : navigateToSignupKeyDown
                }
                css={!isWhiteBoardTemplate && previewSectionStyles}
                role="img"
                aria-label="Template preview"
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex={0}
            >
                <TemplatePreview
                    template={template}
                    isCfind3646Treatment={isCfind3646Treatment}
                />
            </div>
            <TemplateDescription
                template={template}
                isCfind3646Treatment={isCfind3646Treatment}
            />
            <TemplateTabbedSection
                template={template}
                isCfind3646Treatment={isCfind3646Treatment}
            />
            <div css={ctaButtonContainerStyles}>
                <TemplateCtaButtons
                    name={name ?? ""}
                    pluginModuleKey={pluginModuleKey ?? ""}
                />
            </div>
            <TemplateRelatedTemplates template={template} />
        </div>
    );
};
